// The approved service area — which Georgia counties AHFC is approved to serve.
//
// The one owner of that fact. The list lives in public.approved_counties,
// maintained by admins on Settings → Approved Counties; whether a client is in
// the approved area is worked out from their county and that list every time
// it is shown, never stored on the client. (Until 2026-10-20 the list was
// written into countyMapUtil.js and each client carried a hand-set Yes/No that
// disagreed with it for 12 of 33 active clients.)
//
// INTERNAL ONLY. Nothing here may be shown on a page a family, referrer,
// agency or applicant sees — the table is not readable by anonymous requests,
// so a public page could not load it even by mistake.

import { supabase } from './supabase';
import { GA_COUNTIES } from './gaCounties';

const norm = (s) => String(s || '').trim().toLowerCase();

/** The live (not removed) counties in a list of approved_counties rows, sorted. */
export const liveCounties = (rows = []) =>
  (rows || []).filter((r) => r && !r.removed_on).map((r) => r.county).sort((a, b) => a.localeCompare(b));

/**
 * Is this county in the approved area? Case and spacing don't matter.
 * @param {string} county
 * @param {string[]|Set<string>} approved  live county names (from liveCounties)
 */
export const isApprovedCounty = (county, approved = []) => {
  const c = norm(county);
  if (!c) return false;
  for (const a of approved) if (norm(a) === c) return true;
  return false;
};

/** A client's place against the approved area: 'approved' | 'outside' | 'unrecognised' | 'none'. */
export const serviceAreaStatus = (county, approved = []) => {
  const c = norm(county);
  if (!c) return 'none';
  if (!GA_COUNTIES.some((g) => norm(g) === c)) return 'unrecognised';
  return isApprovedCounty(county, approved) ? 'approved' : 'outside';
};

/** Georgia counties that could be added: not already live. */
export const addableCounties = (approved = []) =>
  GA_COUNTIES.filter((g) => !isApprovedCounty(g, approved));

// ── Loading ─────────────────────────────────────────────────────────────────
// Cached for a minute: the dashboard, map and profile all ask, and the list
// changes a few times a year. An edit on the Settings page clears it.
let cache = { at: 0, rows: null };

export const clearServiceAreaCache = () => { cache = { at: 0, rows: null }; };

/** Every approved_counties row, live and removed, newest first. */
export const loadApprovedCountyRows = async () => {
  if (cache.rows && Date.now() - cache.at < 60_000) return { data: cache.rows, error: null };
  const { data, error } = await supabase.from('approved_counties')
    .select('id, county, approved_on, reference, document_url, notes, added_by_name, added_at, removed_on, removed_reason, removed_by_name')
    .order('county');
  if (error) return { data: [], error };
  cache = { at: Date.now(), rows: data || [] };
  return { data: cache.rows, error: null };
};

/** The live approved county names. Empty (with the error) if it cannot be read. */
export const loadApprovedCounties = async () => {
  const { data, error } = await loadApprovedCountyRows();
  return { data: liveCounties(data), error };
};

// ── Changing it (admins only; the database enforces that too) ────────────────

export const addApprovedCounty = async ({ county, approvedOn, reference, documentUrl, notes, profile }) => {
  if (!GA_COUNTIES.includes(county)) return { error: new Error(`${county} is not a Georgia county.`) };
  const { data, error } = await supabase.from('approved_counties').insert({
    county,
    approved_on: approvedOn || null,
    reference: reference?.trim() || null,
    document_url: documentUrl?.trim() || null,
    notes: notes?.trim() || null,
    added_by_id: profile?.id || null,
    added_by_name: profile?.full_name || null,
  }).select().single();
  if (error) return { error };
  clearServiceAreaCache();
  await supabase.from('activity_log').insert({
    activity_type: 'Approved County Added',
    notes: `${county} added to the approved service area${approvedOn ? ` (approved ${approvedOn})` : ''}${reference ? ` — ${reference.trim()}` : ''}.`,
    performed_by_id: profile?.id || null, performed_by_name: profile?.full_name || 'Unknown',
  });
  return { data, error: null };
};

export const updateApprovedCounty = async ({ id, approvedOn, reference, documentUrl, notes }) => {
  const { error } = await supabase.from('approved_counties').update({
    approved_on: approvedOn || null,
    reference: reference?.trim() || null,
    document_url: documentUrl?.trim() || null,
    notes: notes?.trim() || null,
  }).eq('id', id);
  if (!error) clearServiceAreaCache();
  return { error };
};

/** Removing keeps the row, with when, who and why. Its clients become outside. */
export const removeApprovedCounty = async ({ id, county, reason, profile }) => {
  if (!reason?.trim()) return { error: new Error('Say why the county is being removed.') };
  const { error } = await supabase.from('approved_counties').update({
    removed_on: new Date().toISOString().slice(0, 10),
    removed_reason: reason.trim(),
    removed_by_id: profile?.id || null,
    removed_by_name: profile?.full_name || null,
  }).eq('id', id).is('removed_on', null);
  if (error) return { error };
  clearServiceAreaCache();
  await supabase.from('activity_log').insert({
    activity_type: 'Approved County Removed',
    notes: `${county} removed from the approved service area — ${reason.trim()}.`,
    performed_by_id: profile?.id || null, performed_by_name: profile?.full_name || 'Unknown',
  });
  return { error: null };
};
